import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { EmailQuota } from '@account-users/account/models';
import { AccountCategory } from '@account-users/account/models/AccountCategory';
import {
  IAccount,
  HttpAccountFilters,
} from '@account-users/account/models/IAccount';
import { IAccountUser } from '@account-users/account/models/IAccountUser';
import {
  AccountPackageResponse,
  AccountSettingsPayload,
} from '@account-users/account/models/AccountSettingsPayload';
import { AccountSettings } from '@account-users/account/models/AccountSettings';
import { LicenseCode } from '@account-users/account/models/LicenseCode';
import { UserInvitation } from '@account-users/account/models/UserInvitation';
import { AccountCreateRequest } from '@account-users/account/models/AccountCreateRequest';
import {
  LicensePermissionList,
  UserLicencePermission,
} from '@account-users/account/models/LicensePermissionList';
import { License, LicensePackage } from '@account-users/account/models/License';
import { LicenseTypeConfig } from '@account-users/account/models/LicenseTypeConfig';
import { LicenseType } from '@account-users/account/models/LicenseType';
import { AccountLicense } from '@account-users/account/models/AccountLicense';
import { AccountsFilter } from '@account-users/account/models/AccountsFilter';
import { CustomHttpParamEncoder, IMigration } from '@shared';
import { AuthService } from '@account-users/auth/auth.service';

@Injectable({ providedIn: 'root' })
export class AccountService {
  private readonly emailBootstrapUrl = `${this.emailBaseUrl}bootstrap/`;

  constructor(
    private readonly http: HttpClient,
    private authService: AuthService,
    @Inject('apiBaseUrl') private apiBaseUrl: string,
    @Inject('proSyncBaseUrl') private proSyncBaseUrl: string,
    @Inject('emailBaseUrl') private emailBaseUrl: string,
  ) {}

  fetchAccountCategories() {
    return this.http.get(
      `${this.apiBaseUrl}configurations/account-categories`,
    ) as Observable<AccountCategory[]>;
  }

  fetchAccounts(options: AccountsFilter = {}): Observable<IAccount[]> {
    let params: {
      q: string;
      category: string[];
      pageSize?: string;
    } = {
      q: options.query || '',
      category: options.categories || [],
    };
    if (!options.loadAll) {
      params = { ...params, pageSize: '50' };
    }
    if (options.filters) {
      params = { ...params, ...options.filters };
    }
    return this.http.get(`${this.apiBaseUrl}accounts`, {
      params: new HttpParams({
        fromObject: { ...params },
        encoder: new CustomHttpParamEncoder(),
      }),
    }) as Observable<IAccount[]>;
  }

  fetchAccountMigrations(accountId: string) {
    return this.http.get(
      `${this.proSyncBaseUrl}account/${accountId}/migrations`,
      {
        headers: new HttpHeaders({
          Authorization: 'Bearer ' + this.authService.accessToken,
        }),
      },
    ) as Observable<IMigration[]>;
  }

  createAccount(account: AccountCreateRequest) {
    return this.http.post(`${this.apiBaseUrl}accounts`, account) as Observable<
      IAccount
    >;
  }

  deleteAccountAsync(accountId: string) {
    return this.http.delete(`${this.apiBaseUrl}accounts/${accountId}`);
  }

  getAccountUsers(accountId: string, filters?: HttpAccountFilters) {
    return this.http.get(`${this.apiBaseUrl}accounts/${accountId}/users`, {
      params: filters,
    }) as Observable<IAccountUser[]>;
  }

  downloadUserReport(accountId: string) {
    return this.http.get(
      `${this.apiBaseUrl}accounts/${accountId}/users/report`,
      {
        responseType: 'arraybuffer',
      },
    );
  }

  getAccountLicenses(accountId: string, filters?: HttpAccountFilters) {
    return this.http.get<AccountLicense[]>(
      `${this.apiBaseUrl}accounts/${accountId}/licenses`,
      { params: filters },
    );
  }

  putAccountLicense(accountId: string, license: LicenseCode) {
    return this.http.put(
      `${this.apiBaseUrl}accounts/${accountId}/licenses/${license}`,
      {},
    );
  }

  getAccountLicenseState(accountId: string, license: LicenseCode) {
    return this.http.get(
      `${this.apiBaseUrl}accounts/${accountId}/licenses/${license}`,
      {},
    );
  }

  deleteAccountLicense(accountId: string, license: LicenseCode) {
    return this.http.delete(
      `${this.apiBaseUrl}accounts/${accountId}/licenses/${license}`,
      {},
    );
  }
  getAccountDetails(accountId: string, filters?: HttpAccountFilters) {
    return this.http.get(`${this.apiBaseUrl}accounts/${accountId}`, {
      params: filters,
    }) as Observable<IAccount>;
  }

  updateAccountLicensePackage(accountId, orderId, licensePackage) {
    return this.http.patch(
      `${this.apiBaseUrl}accounts/${accountId}/licensepackage`,
      { licensePackage, orderNumber: orderId },
    ) as Observable<AccountPackageResponse[]>;
  }

  getLicensePackages() {
    return this.http.get<LicensePackage[]>(
      `${this.apiBaseUrl}configurations/license-packages`,
    );
  }

  getAllLicenses() {
    return this.http.get<License[]>(`${this.apiBaseUrl}licenses`);
  }

  getLicenseTypes() {
    return this.http.get(
      `${this.apiBaseUrl}configurations/license-types`,
    ) as Observable<LicenseTypeConfig[]>;
  }

  updateAccountDetails(accountId, newDetails) {
    return this.http.patch(
      `${this.apiBaseUrl}accounts/${accountId}`,
      newDetails,
    ) as Observable<IAccount>;
  }

  patchAccountSettings(
    {
      userSeats,
      audiencePackageSize,
      sectionsLimit,
      orderId,
    }: AccountSettingsPayload,
    accountId,
  ) {
    return this.http.patch(
      `${this.apiBaseUrl}accounts/${accountId}/settings?orderId=${orderId}`,
      {
        userSeats,
        audiencePackageSize,
        sectionsLimit,
        orderId,
      },
    ) as Observable<AccountSettings>;
  }

  getAccountSettings(accountId: string) {
    return this.http.get(
      `${this.apiBaseUrl}/accounts/${accountId}/settings`,
    ) as Observable<AccountSettings>;
  }

  getLicensePermissions(licenseTypes: LicenseType[] = []) {
    return this.http.get<LicensePermissionList[]>(
      `${this.apiBaseUrl}configurations/license-permissions`,
      { params: { type: licenseTypes } },
    );
  }

  getLicensePermissionsForAccountUser(accountId, userId) {
    return this.http.get(
      `${this.apiBaseUrl}accounts/${accountId}/users/${encodeURIComponent(
        userId,
      )}/licensepermissions`,
    ) as Observable<UserLicencePermission[]>;
  }

  inviteUser({ accountId, ...invitation }: UserInvitation) {
    return this.http.post(
      `${this.apiBaseUrl}accounts/${accountId}/users`,
      invitation,
    ) as Observable<IAccountUser>;
  }

  bootstrapAccountByEvents(accountId: string, eventDiscriminators: string[]) {
    return this.http.put(
      `${this.emailBootstrapUrl}accounts/${accountId}/events`,
      eventDiscriminators,
    );
  }

  createCrmKeyspace(accountId: string) {
    return this.http.post(
      `${this.apiBaseUrl}accounts/${accountId}/crm-setup`,
      null,
    );
  }

  rebootstrapLicenses(accountId: string) {
    return this.http.post(
      `${this.apiBaseUrl}accounts/${accountId}/licenses/rebootstrap`,
      null,
    );
  }

  accountActivate(accountId: string) {
    return this.http.put(
      `${this.apiBaseUrl}accounts/${accountId}/state/active`,
      null,
    );
  }

  accountLock(accountId: string) {
    return this.http.put(
      `${this.apiBaseUrl}accounts/${accountId}/state/locked`,
      null,
    );
  }

  accountSuspend(accountId: string) {
    return this.http.put(
      `${this.apiBaseUrl}accounts/${accountId}/state/suspend`,
      null,
    );
  }

  accountReactivate(accountId: string) {
    return this.http.put(
      `${this.apiBaseUrl}accounts/${accountId}/state/reactivate`,
      null,
    );
  }

  accountTerminate(accountId: string) {
    return this.http.put(
      `${this.apiBaseUrl}accounts/${accountId}/state/terminate`,
      null,
    );
  }

  accountDelete(accountId: string) {
    return this.http.put(
      `${this.apiBaseUrl}accounts/${accountId}/state/delete`,
      null,
    );
  }

  getEmailQuota(accountId: string): Observable<EmailQuota> {
    return this.http.get<EmailQuota>(
      `${this.apiBaseUrl}accounts/${accountId}/quota`,
    );
  }

  addEmailQuota(
    accountId: string,
    orderId: string,
    quota: EmailQuota,
  ): Observable<EmailQuota> {
    return this.http.patch<EmailQuota>(
      `${this.apiBaseUrl}accounts/${accountId}/quota`,
      { ...quota },
      {
        params: new HttpParams({
          fromObject: { ...{ orderId } },
        }),
      },
    );
  }

  accountDeleteCancel(accountId: string) {
    return this.http.put(
      `${this.apiBaseUrl}/accounts/${accountId}/state/cancel-delete`,
      null,
    );
  }
}
